import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import Preisrunde from "../components/Preisrunde";
import { zahl } from "../lib/zahl";
import { useBestand, eur, datum } from "../hooks/useVertraege";
import type { BestandZeile } from "../hooks/useVertraege";

/**
 * Auswertung (bis 09.10.2026 „Bestand") — der Blick über alle Verträge auf
 * einmal, und darunter die Preisrunde, die aus diesem Blick folgt.
 *
 * Sortiert wird nach **Deckungsbeitrag je Stunde**, nicht nach Umsatz: ein
 * 2.000-€-Vertrag über 40 Stunden ist schlechter als ein 900-€-Vertrag über 8,
 * und im Umsatzranking stünde er oben.
 */
const AMPEL: Record<string, { farbe: string; text: string }> = {
  gruen: { farbe: "var(--vg-green)", text: "trägt sich" },
  gelb: { farbe: "var(--vg-amber)", text: "knapp" },
  rot: { farbe: "var(--pf-red)", text: "unter der Messlatte" },
  unbekannt: { farbe: "var(--pf-ink-3)", text: "keine Zeiten hinterlegt" },
};

export default function BestandPage() {
  const navigate = useNavigate();
  const [ziel, setZiel] = useState("");
  const zielZahl = Number(ziel.replace(",", ".")) || 0;
  const { data, isLoading } = useBestand(zielZahl);

  return (
    <Shell title="Auswertung" subtitle="Deckungsbeitrag, Auslastung und Preisrunde über alle laufenden Verträge">
      {isLoading && <div className="pf-card">Lade…</div>}

      {data && data.anzahl === 0 && (
        <div className="pf-card">
          <div style={{ fontWeight: 700 }}>Noch kein laufender Vertrag</div>
          <div style={{ fontSize: 13, color: "var(--pf-ink-3)", marginTop: 4, lineHeight: 1.6 }}>
            Sobald der erste Vertrag aktiv ist, stehen hier Deckungsbeitrag, Stunden
            und die Verteilung übers Jahr.
          </div>
        </div>
      )}

      {data && data.anzahl > 0 && (
        <>
          <div className="pf-card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700 }}>{data.anzahl} laufende Verträge</div>
            <div style={{ marginTop: 10 }}>
              <Zeile l="Jahresumsatz netto" r={eur(data.jahres_netto)} strong />
              <Zeile l="Direkte Kosten" r={eur(data.jahres_kosten)} />
              <Zeile l="Deckungsbeitrag"
                r={`${eur(data.deckungsbeitrag)}${data.db_prozent ? ` · ${zahl(data.db_prozent)} %` : ""}`}
                strong />
              <Zeile l="Arbeitszeit im Jahr" r={`${zahl(data.jahres_stunden, 1)} Std.`} />
              <Zeile l="Deckungsbeitrag je Stunde"
                r={data.db_pro_stunde ? eur(data.db_pro_stunde) : "—"} strong />
            </div>
            <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 10, lineHeight: 1.55 }}>
              Direkte Kosten sind Material und Entsorgung aus der Kalkulation — Löhne,
              Fahrzeuge und Gemeinkosten müssen aus dem Deckungsbeitrag noch bezahlt werden.
            </div>
          </div>

          <div className="pf-card" style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12.5, color: "var(--pf-ink-2)" }}>
              Ziel-Deckungsbeitrag je Stunde (€, leer lassen = Vergleich mit dem eigenen Schnitt)
            </label>
            <input className="pf-input" type="number" inputMode="decimal" min="0" step="1"
              value={ziel} placeholder="z. B. 65" onChange={e => setZiel(e.target.value)} />
            <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 8, lineHeight: 1.55 }}>
              {data.referenz_art === "ziel"
                ? `Gemessen wird gegen ${eur(data.referenz_db_stunde)} je Stunde.`
                : `Ohne Ziel wird gegen den Median deines Bestands gemessen (${eur(data.referenz_db_stunde)} je Stunde). Das zeigt, was unter deinem eigenen Schnitt liegt — es ist keine Aussage darüber, ob sich ein Vertrag rechnet.`}
              {" "}Rot ab 20 % unter der Messlatte.
            </div>
          </div>

          {(data.rot > 0 || data.unbekannt > 0) && (
            <div className="pf-card" style={{ marginBottom: 16, borderLeft: "3px solid var(--pf-red)" }}>
              {data.rot > 0 && (
                <div style={{ fontSize: 14 }}>
                  <strong>{data.rot}</strong>{" "}
                  {data.rot === 1 ? "Vertrag liegt" : "Verträge liegen"} deutlich unter der
                  Messlatte. Beim nächsten Vertragsjahr ist dort der Hebel — nicht beim Umsatz.
                </div>
              )}
              {data.unbekannt > 0 && (
                <div style={{ fontSize: 13, color: "var(--pf-ink-2)", marginTop: data.rot > 0 ? 8 : 0 }}>
                  Bei {data.unbekannt} {data.unbekannt === 1 ? "Vertrag" : "Verträgen"} fehlen
                  Zeiten in der Kalkulation — ohne sie lässt sich nichts über die Stunde sagen.
                </div>
              )}
            </div>
          )}

          <div className="pf-card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700 }}>Auslastung übers Jahr</div>
            <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 4 }}>
              Geplante Arbeitszeit je Monat aus den Einsatzmonaten der Verträge.
            </div>
            <Auslastung zeilen={data.auslastung} />
          </div>

          <div style={{ fontWeight: 700, margin: "18px 0 8px" }}>Verträge nach Deckungsbeitrag je Stunde</div>
          {data.vertraege.map(z => (
            <BestandZeileKarte key={z.id} z={z} onOeffnen={() => navigate(`/vertraege/${z.id}`)} />
          ))}
        </>
      )}

      <Preisrunde />
    </Shell>
  );
}

function Auslastung({ zeilen }: { zeilen: { kurz: string; stunden: string; einsaetze: number }[] }) {
  const werte = zeilen.map(z => Number(z.stunden));
  const max = Math.max(...werte, 1);
  return (
    <div style={{ marginTop: 12 }}>
      {zeilen.map(z => (
        <div key={z.kurz} style={{ display: "flex", alignItems: "center", gap: 10, padding: "3px 0" }}>
          <div style={{ width: 78, fontSize: 12.5, color: "var(--pf-ink-2)", flexShrink: 0 }}>
            {z.kurz}
          </div>
          <div style={{ flex: 1, height: 14, background: "var(--pf-line)", borderRadius: 3 }}>
            <div style={{
              width: `${(Number(z.stunden) / max) * 100}%`, height: "100%",
              background: "var(--pf-accent)", borderRadius: 3,
            }} />
          </div>
          <div style={{ width: 74, textAlign: "right", fontSize: 12.5, whiteSpace: "nowrap" }}>
            {zahl(z.stunden, 1)} Std.
          </div>
        </div>
      ))}
    </div>
  );
}

function BestandZeileKarte({ z, onOeffnen }: { z: BestandZeile; onOeffnen: () => void }) {
  const a = AMPEL[z.ampel] ?? AMPEL.unbekannt;
  return (
    <button className="pf-card" onClick={onOeffnen} style={{
      display: "block", width: "100%", textAlign: "left", marginBottom: 10,
      cursor: "pointer", font: "inherit", color: "inherit",
      borderLeft: `3px solid ${a.farbe}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{z.kunde_name || "—"}</div>
          <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)" }}>
            {z.nummer} · seit {datum(z.beginn)} · {eur(z.jahres_netto)} netto/Jahr
            {z.preisanpassung_zum ? ` · Anpassung ${datum(z.preisanpassung_zum)}` : ""}
          </div>
        </div>
        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          <div style={{ fontWeight: 700, color: a.farbe }}>
            {z.db_pro_stunde ? `${eur(z.db_pro_stunde)}/Std.` : "—"}
          </div>
          <div style={{ fontSize: 12, color: "var(--pf-ink-3)" }}>
            {zahl(z.jahres_zeit_stunden, 1)} Std. · {a.text}
          </div>
        </div>
      </div>
    </button>
  );
}

function Zeile({ l, r, strong }: { l: string; r: string; strong?: boolean }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0",
      borderTop: "1px solid var(--pf-line)", fontSize: 14,
    }}>
      <span style={{ color: "var(--pf-ink-2)" }}>{l}</span>
      <span style={{ fontWeight: strong ? 700 : 500, whiteSpace: "nowrap" }}>{r}</span>
    </div>
  );
}
